import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { MediaItem, MediaFile } from '../types';

interface TempMediaMetadata {
  postId: string;
  postDocPath: string;
  originalUrls: string[];
  downloadedUrls: string[];
  createdAt: number;
  expiresAt: number; // 14 days from creation
}

/**
 * Downloads a file from a URL and returns it as a Blob
 */
async function downloadFile(url: string): Promise<Blob> {
  try {
    // Use proxy to download the file
    const response = await fetch(`https://api.sellox.io/api/proxy?url=${encodeURIComponent(url)}`);
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status}`);
    }
    return await response.blob();
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
}

/**
 * Uploads a blob to Firebase Storage in the temp_media folder
 */
async function uploadToStorage(blob: Blob, postId: string, mediaIndex: number, fileType: string): Promise<string> {
  const extension = fileType === 'video' ? 'mp4' : 'jpg';
  const fileName = `${postId}_${mediaIndex}.${extension}`;
  const storageRef = ref(storage, `temp_media/${fileName}`);
  
  await uploadBytes(storageRef, blob, {
    contentType: fileType === 'video' ? 'video/mp4' : 'image/jpeg',
  });
  
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
}

/**
 * Downloads all media for a post and stores it in Firebase Storage
 * Returns the updated MediaItem with new URLs
 * 
 * This function performs all downloads and uploads first, then does a single
 * batched write to Firestore to ensure atomicity.
 */
export async function downloadAndStoreMedia(item: MediaItem): Promise<MediaItem> {
  if (!item.docPath) {
    throw new Error('Post does not have a docPath');
  }

  console.log(`Starting download process for post ${item.id}`);

  const originalUrls: string[] = [];
  const downloadedUrls: string[] = [];
  const updatedMedia: MediaFile[] = [];

  // PHASE 1: Download and upload all main media files
  console.log(`Phase 1: Processing ${item.media.length} main media files...`);
  for (let i = 0; i < item.media.length; i++) {
    const mediaFile = item.media[i];
    originalUrls.push(mediaFile.downloadURL);

    try {
      console.log(`Downloading media ${i + 1}/${item.media.length}: ${mediaFile.type}`);
      // Download the file
      const blob = await downloadFile(mediaFile.downloadURL);
      
      console.log(`Uploading media ${i + 1}/${item.media.length} to Firebase Storage...`);
      // Upload to Firebase Storage
      const newUrl = await uploadToStorage(blob, item.id, i, mediaFile.type);
      downloadedUrls.push(newUrl);

      // Update media file with new URL
      updatedMedia.push({
        ...mediaFile,
        downloadURL: newUrl,
        path: newUrl,
      });
      console.log(`Successfully processed media ${i + 1}/${item.media.length}`);
    } catch (error) {
      console.error(`Error processing media ${i} for post ${item.id}:`, error);
      // Keep original URL if download fails
      updatedMedia.push(mediaFile);
      downloadedUrls.push(mediaFile.downloadURL);
    }
  }

  // PHASE 2: Process child posts if they exist (for Sidecar posts)
  let updatedChildPosts = item.childPosts;
  if (item.childPosts && item.childPosts.length > 0) {
    console.log(`Phase 2: Processing ${item.childPosts.length} child posts...`);
    updatedChildPosts = [];
    for (let i = 0; i < item.childPosts.length; i++) {
      const child = item.childPosts[i];
      
      try {
        console.log(`Processing child post ${i + 1}/${item.childPosts.length}...`);
        // Download and upload displayUrl
        const displayBlob = await downloadFile(child.displayUrl);
        const newDisplayUrl = await uploadToStorage(
          displayBlob,
          `${item.id}_child_${i}`,
          0,
          'image'
        );

        // Build child update object, avoiding undefined values
        const childUpdate: any = {
          id: child.id,
          displayUrl: newDisplayUrl,
          type: child.type,
        };

        // Include url if it exists
        if (child.url) {
          childUpdate.url = child.url;
        }

        // Only include videoUrl if it exists and we downloaded it
        if (child.videoUrl) {
          console.log(`Processing video for child post ${i + 1}...`);
          const videoBlob = await downloadFile(child.videoUrl);
          const newVideoUrl = await uploadToStorage(
            videoBlob,
            `${item.id}_child_${i}`,
            1,
            'video'
          );
          childUpdate.videoUrl = newVideoUrl;
        }

        updatedChildPosts.push(childUpdate);
        console.log(`Successfully processed child post ${i + 1}/${item.childPosts.length}`);
      } catch (error) {
        console.error(`Error processing child post ${i} for post ${item.id}:`, error);
        updatedChildPosts.push(child);
      }
    }
  }

  // PHASE 3: Prepare batch write data (only after all downloads complete)
  console.log('Phase 3: All downloads complete. Preparing batch Firestore update...');
  const now = Date.now();
  const expiresAt = now + (14 * 24 * 60 * 60 * 1000); // 14 days from now
  
  const metadata: TempMediaMetadata = {
    postId: item.id,
    postDocPath: item.docPath,
    originalUrls,
    downloadedUrls,
    createdAt: now,
    expiresAt,
  };

  // Prepare post update data
  const updateData: any = {
    media: updatedMedia,
    mediaDownloaded: true,
  };

  // Update displayUrl if it's the first media item (only if it has a valid value)
  if (updatedMedia.length > 0 && updatedMedia[0].primary && updatedMedia[0].downloadURL) {
    updateData.displayUrl = updatedMedia[0].downloadURL;
  }

  // Update videoUrl if it's a video post (only if video exists and has valid URL)
  if (item.type === 'Video' && updatedMedia.length > 1 && updatedMedia[1].downloadURL) {
    updateData.videoUrl = updatedMedia[1].downloadURL;
  }

  // Update child posts if they exist and array is not empty
  // Important: Only set if we actually have child posts to avoid setting undefined
  if (updatedChildPosts && Array.isArray(updatedChildPosts) && updatedChildPosts.length > 0) {
    updateData.childPosts = updatedChildPosts;
  }

  // PHASE 4: Perform atomic batch write to Firestore
  console.log('Phase 4: Executing atomic batch write to Firestore...');
  const batch = writeBatch(db);
  
  // Add metadata document to batch
  const metadataRef = doc(db, 'temp_media', item.id);
  batch.set(metadataRef, metadata);
  
  // Add post update to batch
  const postRef = doc(db, item.docPath);
  batch.update(postRef, updateData);
  
  // Commit the batch (atomic operation - all or nothing)
  await batch.commit();
  
  console.log(`Successfully completed download and update for post ${item.id}`);

  // Return updated item
  return {
    ...item,
    media: updatedMedia,
    mediaDownloaded: true,
    childPosts: updatedChildPosts,
  };
}

/**
 * Checks if a post's media has already been downloaded
 */
export function isMediaDownloaded(item: MediaItem): boolean {
  return item.mediaDownloaded === true;
}

/**
 * Gets the download status for display
 */
export function getDownloadStatus(item: MediaItem): 'downloaded' | 'pending' {
  return isMediaDownloaded(item) ? 'downloaded' : 'pending';
}
